'use client';

import {
  AnimationEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/drawer';
import { useIsMobile } from '@hooks/use-mobile';
import { cn } from '@utils/cn';

/** Vaul's open animation (TRANSITIONS.DURATION) plus a frame of slack, used
 * when `animationend` never fires (reduced motion, `data-vaul-animate=false`). */
const SHEET_OPEN_SETTLE_MS = 550;

interface ResponsiveOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /**
   * Actions. On phone they sit in a bar pinned below the scrolling body (so
   * they stay reachable above long content and the keyboard); on desktop
   * they render in the dialog footer.
   */
  footer?: ReactNode;
  contentClassName?: string;
  presentation?: 'auto' | 'sheet' | 'dialog' | 'fullscreen';
  /**
   * Element to focus once the overlay is ready. Use this instead of
   * `autoFocus` on the first field: on phone, focusing while the sheet is
   * still sliding in makes iOS scroll the page toward the off-screen input.
   * The sheet focuses it after its open animation; the desktop dialog
   * focuses it immediately, as `autoFocus` would.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Runs when focus would return to the trigger after closing. Call
   * `event.preventDefault()` and focus something else when the action moved
   * the person on (e.g. to a section the sheet just created).
   */
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * The one overlay primitive screens should import: bottom sheet (Vaul) on
 * phone, centered Radix Dialog on desktop. Same open/onOpenChange contract.
 */
export function ResponsiveOverlay({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  contentClassName,
  presentation = 'auto',
  initialFocusRef,
  onCloseAutoFocus
}: ResponsiveOverlayProps) {
  const isMobile = useIsMobile();
  const renderSheet =
    presentation === 'sheet' || (presentation === 'auto' && isMobile);
  const focusedForOpen = useRef(false);

  const focusInitial = useCallback(() => {
    if (focusedForOpen.current) return;
    focusedForOpen.current = true;
    initialFocusRef?.current?.focus({ preventScroll: true });
  }, [initialFocusRef]);

  useEffect(() => {
    if (!open) {
      focusedForOpen.current = false;
      return;
    }
    if (!renderSheet || !initialFocusRef) return;
    // Fallback for when the slide-in animation never reports its end.
    const timer = window.setTimeout(focusInitial, SHEET_OPEN_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [open, renderSheet, initialFocusRef, focusInitial]);

  if (renderSheet) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className={cn('rounded-t-xl pb-0', contentClassName)}
          onCloseAutoFocus={onCloseAutoFocus}
          onAnimationEnd={(event: AnimationEvent<HTMLDivElement>) => {
            if (event.target === event.currentTarget && open && initialFocusRef)
              focusInitial();
          }}
        >
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            {description ? (
              <DrawerDescription>{description}</DrawerDescription>
            ) : null}
          </DrawerHeader>
          <div
            data-overlay-body=""
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4',
              footer ? 'pb-4' : 'pb-[calc(var(--safe-bottom)+1rem)]'
            )}
          >
            {children}
          </div>
          {footer ? (
            <div
              data-overlay-footer=""
              className="flex shrink-0 flex-col-reverse gap-2 border-t border-divider bg-popover px-4 pt-3 pb-[calc(var(--safe-bottom)+1rem)]"
            >
              {footer}
            </div>
          ) : null}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onCloseAutoFocus={onCloseAutoFocus}
        onOpenAutoFocus={event => {
          const target = initialFocusRef?.current;
          if (!target) return;
          event.preventDefault();
          focusedForOpen.current = true;
          target.focus();
        }}
        className={cn(
          presentation === 'fullscreen' &&
            'inset-0 h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto sm:rounded-none',
          contentClassName
        )}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        {children}
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}
